import { Container } from '@tryhackme/thm-ui-components';
import { faBolt, faAward } from '@fortawesome/pro-solid-svg-icons';
import { useParams, Navigate, Link } from 'react-router-dom';
import { ReactComponent as TrophyStar } from 'src/images/svgs/personal-badges/trophy-star.svg';
import { SEO } from 'src/common/components/seo';
import { DEFAULT_META_DESC } from 'src/common/components/seo/seo.constants';
import { StyledHexagon, StyledHexagonContainer, StyledFontAwesomeIcon, StyledLogo, StyledPersonalBadgesContent, StyledSectionWrapper, StyledSkeleton } from './personal-badges.styles';
import { useGetBadgeByUserAndBadgeNameQuery } from './personal-badges.slice';
import { PersonalBadgeData } from './personal-badges.types';
const LoadingView = () => {
  const skeletonKeys = ['loading-card-1', 'loading-card-2', 'loading-card-3'];
  return <>
      <div role="status" aria-label="Loading personal badge info" className="left-side">
        <header className="top">
          <StyledSkeleton width="230px" height="24px" data-sentry-element="StyledSkeleton" data-sentry-source-file="personal-badges.tsx" />
        </header>
        <section>
          <div>
            <StyledSkeleton width="650px" height="110px" data-sentry-element="StyledSkeleton" data-sentry-source-file="personal-badges.tsx" />
          </div>
          <div className="cards">
            {skeletonKeys.map(key => <StyledSkeleton width="206px" height="101px" key={key} />)}
          </div>
        </section>
        <footer>
          <span>Come learn all things security at </span>
          <Link to="https://tryhackme.com" data-sentry-element="Link" data-sentry-source-file="personal-badges.tsx">TryHackMe</Link>
        </footer>
      </div>
      <StyledHexagonContainer data-sentry-element="StyledHexagonContainer" data-sentry-source-file="personal-badges.tsx">
        <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx" />
        <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx" />
        <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx" />
      </StyledHexagonContainer>
    </>;
};
const LiveView = ({
  data
}: {
  data: PersonalBadgeData;
}) => <>
    <article className="left-side">
      <header className="top">
        <Link to={`/p/${data?.username}`} className="username" data-sentry-element="Link" data-sentry-source-file="personal-badges.tsx">
          {data?.username}
        </Link>
        <span> was awarded a badge:</span>
      </header>
      <section>
        <div>
          <h1>{data?.title}</h1>
          <h2>{data?.description}</h2>
        </div>
        <div className="cards">
          <div className="card">
            <span className="title">Rank</span>
            <div className="content">
              <span>
                <TrophyStar data-sentry-element="TrophyStar" data-sentry-source-file="personal-badges.tsx" />
              </span>
              <span>{data?.rank}</span>
            </div>
          </div>
          <div className="card">
            <span className="title">Level</span>
            <div className="content">
              <span>
                <StyledFontAwesomeIcon icon={faBolt} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="personal-badges.tsx" />
              </span>
              <span>{data?.level}</span>
            </div>
          </div>
          <div className="card">
            <span className="title">Badges</span>
            <div className="content">
              <span>
                <StyledFontAwesomeIcon icon={faAward} data-sentry-element="StyledFontAwesomeIcon" data-sentry-source-file="personal-badges.tsx" />
              </span>
              <span>
                {data?.acquiredBadgeCount}/{data?.totalBadgeCount}
              </span>
            </div>
          </div>
        </div>
      </section>
      <footer>
        <span>Come learn all things security at </span>
        <a href="https://tryhackme.com">TryHackMe</a>
      </footer>
    </article>
    <StyledHexagonContainer data-sentry-element="StyledHexagonContainer" data-sentry-source-file="personal-badges.tsx">
      <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx" />
      <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx">{!!data?.name && <img src={data?.image} alt={`${data?.name} badge`} />}</StyledHexagon>
      <StyledHexagon data-sentry-element="StyledHexagon" data-sentry-source-file="personal-badges.tsx" />
    </StyledHexagonContainer>
  </>;
export const PersonalBadges = () => {
  let {
    username,
    badgeName
  } = useParams();
  if (!username) username = '';
  if (!badgeName) badgeName = '';
  const {
    data: badgeInfo,
    isLoading,
    isError
  } = useGetBadgeByUserAndBadgeNameQuery({
    username,
    badgeName
  });
  if (!isLoading && isError) return <Navigate to="/404" replace />;
  return <StyledSectionWrapper data-sentry-element="StyledSectionWrapper" data-sentry-component="PersonalBadges" data-sentry-source-file="personal-badges.tsx">
      <SEO title="User Badge" description={isLoading || !badgeInfo?.data ? DEFAULT_META_DESC : badgeInfo?.data?.meta?.description} imageURL={isLoading || !badgeInfo?.data ? 'https://tryhackme.com/img/logo/thm_logo_circle.png' : badgeInfo?.data?.meta?.image} data-sentry-element="SEO" data-sentry-source-file="personal-badges.tsx" />
      <Container data-sentry-element="Container" data-sentry-source-file="personal-badges.tsx">
        <StyledLogo data-sentry-element="StyledLogo" data-sentry-source-file="personal-badges.tsx">
          <Link to="https://tryhackme.com" data-sentry-element="Link" data-sentry-source-file="personal-badges.tsx">
            <img src="https://assets.tryhackme.com/img/logo/tryhackme_logo_full.svg" width="141px" height="80px" alt="TryHackMe" />
          </Link>
        </StyledLogo>
        <StyledPersonalBadgesContent data-sentry-element="StyledPersonalBadgesContent" data-sentry-source-file="personal-badges.tsx">
          {isLoading || !badgeInfo?.data ? <LoadingView /> : <LiveView data={badgeInfo.data} />}
        </StyledPersonalBadgesContent>
      </Container>
    </StyledSectionWrapper>;
};